import type {
  Archetype,
  Checkpoint,
  GraphRecords,
  IOMapping,
  MaterialNodeSpec,
  OperationNodeSpec,
  TransferOptions,
  TransferRow,
} from '../../../graph/domain/types';
import { generateGuid } from '../../../graph/utils/id';
import { validateIOMapping } from '../complexHelpers';
import {
  buildDestinationPostState,
  buildSourcePostState,
  quantityTypeFromUnit,
  type NewDestinationDraft,
  type TransferRowFormState,
} from '../transferHelpers';
import { buildInputReminderNode } from '../complexHelpers';
import {
  allEdgeKeysOf,
  collectDownstreamCascade,
  consumersOf,
  inputEdgesOf,
  isReminderMaterial,
  otherProducersOf,
  outputEdgesOf,
  resolvePrimaryInput,
  resolvePrimaryOutput,
  topologyClassOf,
  type TopologyClass,
} from './topology';

// ── Public types ────────────────────────────────────────────────────────────

/** The subset of an `OperationTypeDefinition` a conversion actually needs. */
export interface ConversionTarget {
  archetype: Archetype;
  operationTypeId?: string;
  displayName?: string;
  durationAllow?: boolean;
  checkpoints?: Checkpoint[];
}

export interface ConversionExtras {
  /** Required when converting *into* TRANSFER — supplies quantity or symbolic. */
  transferRow?: TransferRowFormState;
  /** Existing material to use as the transfer destination. */
  destinationMaterialId?: string;
  /** Draft for a brand-new destination material (no pre-state edge). */
  destinationDraft?: NewDestinationDraft;
  /** Explicit IO mapping when converting into COMPLEX; defaulted when absent. */
  ioMapping?: IOMapping[];
}

export interface PlannedEdge {
  sourceId: string;
  targetId: string;
}

export interface PlannedMaterial {
  id: string;
  spec: MaterialNodeSpec;
}

export interface ConversionPlan {
  operationId: string;
  from: Archetype;
  to: Archetype;
  fromClass: TopologyClass;
  toClass: TopologyClass;
  /** True when nothing needs to change — the archetype is already the target. */
  isNoop: boolean;

  /** Every `flowEdges` Record key belonging to the operation, all removed. */
  edgeKeysToRemove: string[];
  /**
   * The operation's complete post-conversion edge set, in canonical order.
   * Order is load-bearing: `hydrateFromSnapshot` and the backend both read
   * `inputs[0]`/`inputs[1]` and `outputs[0]`/`outputs[1]` positionally.
   */
  edgesToAdd: PlannedEdge[];

  materialsToCreate: PlannedMaterial[];
  /** In-place edits to surviving materials (re-roled post-states, reminders). */
  materialPatches: Array<{ id: string; patch: Partial<MaterialNodeSpec> }>;
  /** Deleted outright, downstream cascade included. */
  materialsToDelete: string[];
  /** Operations swept up by that downstream cascade. */
  operationsToDelete: string[];
  /** Kept in the graph but disconnected from this operation. */
  materialsToDetach: string[];

  /** Structural fields the conversion owns; applied over the caller's patch. */
  specPatch: Partial<OperationNodeSpec>;
  /** Stale archetype fields, cleared last so nothing can sneak back. */
  clearFields: Array<keyof OperationNodeSpec>;

  /** True when a non-reminder material is destroyed — gate the confirm dialog. */
  isLossy: boolean;
  warnings: string[];
  /** Non-empty means the conversion cannot proceed. */
  blockers: string[];
}

export interface PlanDeps {
  generateId: () => string;
}

const REMINDER_DX = 80;
const POST_DY = 120;
const POST_DX = 140;

// ── Planner ─────────────────────────────────────────────────────────────────

/**
 * Work out everything an archetype change implies, without touching the store.
 *
 * The rule that drives every narrowing conversion is the same: keep the primary
 * input and the primary output, detach the other inputs (they belong to an
 * upstream producer), and delete the other outputs together with whatever hangs
 * off them. Reminder materials are bookkeeping — they are deleted when nothing
 * consumes them and detached when something does.
 */
export function planArchetypeConversion(
  graphData: GraphRecords,
  operationId: string,
  target: ConversionTarget,
  extras: ConversionExtras = {},
  deps: PlanDeps = { generateId: generateGuid },
): ConversionPlan {
  const operation = graphData.operationNodes[operationId];
  if (!operation) throw new Error(`Operation not found: ${operationId}`);

  const from = operation.archetype;
  const to = target.archetype;
  const fromClass = topologyClassOf(from);
  const toClass = topologyClassOf(to);

  const plan: ConversionPlan = {
    operationId,
    from,
    to,
    fromClass,
    toClass,
    isNoop: from === to,
    edgeKeysToRemove: [],
    edgesToAdd: [],
    materialsToCreate: [],
    materialPatches: [],
    materialsToDelete: [],
    operationsToDelete: [],
    materialsToDetach: [],
    specPatch: {},
    clearFields: [],
    isLossy: false,
    warnings: [],
    blockers: [],
  };

  if (plan.isNoop) return plan;

  plan.specPatch.archetype = to;
  plan.clearFields = staleFieldsFor(to, toClass, target);

  const inputs = inputEdgesOf(graphData, operationId);
  const outputs = outputEdgesOf(graphData, operationId);
  const reminderOutputs = outputs.filter((o) =>
    isReminderMaterial(graphData.materialNodes[o.materialId]),
  );
  const productOutputs = outputs.filter(
    (o) => !isReminderMaterial(graphData.materialNodes[o.materialId]),
  );

  // Which materials survive as inputs / outputs of the converted operation.
  const keptInputIds = resolveKeptInputs(graphData, operation, operationId, toClass, inputs);
  const keptOutputIds = resolveKeptOutputs(
    graphData,
    operation,
    operationId,
    toClass,
    productOutputs,
  );

  // Inputs that lose their edge are only detached — an input material is owned
  // by whatever produced it, never by this operation.
  for (const input of inputs) {
    if (!keptInputIds.includes(input.materialId)) {
      plan.materialsToDetach.push(input.materialId);
    }
  }

  // Dropped product outputs are destroyed along with everything downstream.
  const doomedOutputIds: string[] = [];
  for (const output of productOutputs) {
    if (keptOutputIds.includes(output.materialId)) continue;
    if (otherProducersOf(graphData, output.materialId, operationId).length > 0) {
      plan.materialsToDetach.push(output.materialId);
      plan.warnings.push(
        `"${nameOf(graphData, output.materialId)}" is also produced elsewhere, so it was disconnected rather than deleted.`,
      );
      continue;
    }
    doomedOutputIds.push(output.materialId);
  }

  if (doomedOutputIds.length > 0) {
    const cascade = collectDownstreamCascade(graphData, doomedOutputIds);
    plan.materialsToDelete.push(...cascade.materialIds);
    plan.operationsToDelete.push(...cascade.operationIds);
  }

  // Reminders are regenerated by the target archetype (or dropped entirely).
  const reusableReminderIds = planReminderDisposal(
    graphData,
    operationId,
    toClass,
    reminderOutputs,
    plan,
  );

  // Build the target topology.
  if (toClass === 'SIMPLE') {
    planSimpleTopology(plan, keptInputIds, keptOutputIds);
  } else if (toClass === 'TRANSFER') {
    planTransferTopology(
      graphData,
      operation,
      operationId,
      plan,
      keptInputIds,
      keptOutputIds,
      extras,
      deps,
    );
  } else {
    planComplexTopology(
      graphData,
      operationId,
      plan,
      keptInputIds,
      keptOutputIds,
      reusableReminderIds,
      extras,
      deps,
    );
  }

  plan.edgeKeysToRemove = allEdgeKeysOf(graphData, operationId);

  // A material dropped as an input can come back in another role — most often
  // an existing COMPLEX input reused as the transfer destination. Anything still
  // wired to the operation is not detached, and must not be reported as such.
  const stillConnected = new Set<string>();
  for (const edge of plan.edgesToAdd) {
    stillConnected.add(edge.sourceId === operationId ? edge.targetId : edge.sourceId);
  }
  plan.materialsToDetach = dedupe(
    plan.materialsToDetach.filter((id) => !stillConnected.has(id)),
  );
  plan.materialsToDelete = dedupe(
    plan.materialsToDelete.filter((id) => !stillConnected.has(id)),
  );
  plan.operationsToDelete = dedupe(plan.operationsToDelete);
  plan.isLossy = plan.materialsToDelete.some(
    (id) => !isReminderMaterial(graphData.materialNodes[id]),
  );

  if (plan.edgesToAdd.filter((e) => e.targetId === operationId).length === 0) {
    plan.blockers.push('The operation would be left with no input material.');
  }
  if (plan.edgesToAdd.filter((e) => e.sourceId === operationId).length === 0) {
    plan.blockers.push('The operation would be left with no output material.');
  }

  return plan;
}

// ── Kept-material resolution ────────────────────────────────────────────────

function resolveKeptInputs(
  graphData: GraphRecords,
  operation: OperationNodeSpec,
  operationId: string,
  toClass: TopologyClass,
  inputs: ReturnType<typeof inputEdgesOf>,
): string[] {
  if (toClass === 'COMPLEX') {
    // COMPLEX accepts any arity — nothing has to be dropped.
    return inputs.map((i) => i.materialId);
  }

  const primary = resolvePrimaryInput(graphData, operation, operationId);
  if (!primary) return [];

  if (toClass === 'SIMPLE') return [primary];

  // TRANSFER: the source plus, at most, one existing destination. The
  // destination itself is resolved from `extras` in planTransferTopology.
  return [primary];
}

function resolveKeptOutputs(
  graphData: GraphRecords,
  operation: OperationNodeSpec,
  operationId: string,
  toClass: TopologyClass,
  productOutputs: ReturnType<typeof outputEdgesOf>,
): string[] {
  if (toClass === 'COMPLEX') {
    return productOutputs.map((o) => o.materialId);
  }

  const primary = resolvePrimaryOutput(graphData, operation, operationId);
  if (!primary) return [];
  if (!productOutputs.some((o) => o.materialId === primary)) return [];
  return [primary];
}

// ── Reminder disposal ───────────────────────────────────────────────────────

/**
 * Decide what happens to the operation's existing reminder outputs.
 * Returns the ids that the target archetype may reuse as its own reminders.
 */
function planReminderDisposal(
  graphData: GraphRecords,
  operationId: string,
  toClass: TopologyClass,
  reminderOutputs: ReturnType<typeof outputEdgesOf>,
  plan: ConversionPlan,
): string[] {
  const reusable: string[] = [];

  for (const reminder of reminderOutputs) {
    const consumed = consumersOf(graphData, reminder.materialId, operationId).length > 0;

    if (toClass === 'COMPLEX') {
      // Entering COMPLEX: keep the node and re-point it at an input, exactly as
      // editComplexStep reuses reminders across edits.
      reusable.push(reminder.materialId);
      continue;
    }

    if (consumed) {
      plan.materialsToDetach.push(reminder.materialId);
      plan.warnings.push(
        `Remainder "${nameOf(graphData, reminder.materialId)}" is consumed by a later step, so it was kept and disconnected instead of deleted.`,
      );
      continue;
    }

    plan.materialsToDelete.push(reminder.materialId);
  }

  return reusable;
}

// ── Per-class topology builders ─────────────────────────────────────────────

function planSimpleTopology(
  plan: ConversionPlan,
  keptInputIds: string[],
  keptOutputIds: string[],
): void {
  const input = keptInputIds[0];
  const output = keptOutputIds[0];
  if (input) plan.edgesToAdd.push({ sourceId: input, targetId: plan.operationId });
  if (output) plan.edgesToAdd.push({ sourceId: plan.operationId, targetId: output });
}

function planTransferTopology(
  graphData: GraphRecords,
  operation: OperationNodeSpec,
  operationId: string,
  plan: ConversionPlan,
  keptInputIds: string[],
  keptOutputIds: string[],
  extras: ConversionExtras,
  deps: PlanDeps,
): void {
  const sourceId = keptInputIds[0];
  const source = sourceId ? graphData.materialNodes[sourceId] : undefined;
  if (!source) {
    plan.blockers.push('Cannot find a source material for the transfer.');
    return;
  }

  const row = extras.transferRow;
  if (!row) {
    plan.blockers.push('Transfer details are required before changing to a transfer step.');
    return;
  }
  const hasQuantity = row.mode === 'numeric' && row.amount != null && row.unit != null;
  const hasSymbolic = row.mode === 'symbolic' && row.symbolic != null;
  if (!hasQuantity && !hasSymbolic) {
    plan.blockers.push('A transfer needs either a quantity or a symbolic amount.');
    return;
  }

  // Destination: an existing material (which gets a pre-state edge) or a draft
  // (which does not) — matching addTransferStep.
  let destinationId: string | null = null;
  let destinationIsExisting = false;

  if (extras.destinationMaterialId && graphData.materialNodes[extras.destinationMaterialId]) {
    destinationId = extras.destinationMaterialId;
    destinationIsExisting = true;
  } else if (extras.destinationDraft) {
    destinationId = deps.generateId();
    plan.materialsToCreate.push({
      id: destinationId,
      spec: {
        name: extras.destinationDraft.name,
        container: extras.destinationDraft.container,
        constituents: [],
        quantity: null,
        isEmpty: true,
        position: offset(operation, -POST_DX, -POST_DY),
      },
    });
  } else {
    plan.blockers.push('A destination material is required for a transfer step.');
    return;
  }

  const destination = destinationIsExisting ? graphData.materialNodes[destinationId!] : null;

  // The existing product output carries the downstream subgraph, so it becomes
  // the merged destination post-state. The remainder is always new.
  const destPostId = keptOutputIds[0] ?? deps.generateId();
  const destPostState = buildDestinationPostState(source, destination, row, destinationId!);
  if (keptOutputIds[0]) {
    // Re-role the existing product output so its downstream subgraph survives.
    // Only the quantitative fields move, matching editTransferStep — the name
    // and position are identity that later steps already reference.
    plan.materialPatches.push({
      id: destPostId,
      patch: {
        quantity: destPostState.quantity,
        constituents: destPostState.constituents,
        symbolicQuantity: destPostState.symbolicQuantity,
        sourceMaterials: destPostState.sourceMaterials,
      },
    });
  } else {
    plan.materialsToCreate.push({
      id: destPostId,
      spec: { ...destPostState, position: offset(operation, POST_DX / 2, POST_DY) },
    });
  }

  const sourcePostId = deps.generateId();
  plan.materialsToCreate.push({
    id: sourcePostId,
    spec: {
      ...buildSourcePostState(source, row),
      position: offset(operation, -POST_DX / 2, POST_DY),
    },
  });

  // Canonical order: source, destination — then remainder, merged.
  plan.edgesToAdd.push({ sourceId, targetId: operationId });
  if (destinationIsExisting) {
    plan.edgesToAdd.push({ sourceId: destinationId!, targetId: operationId });
  }
  plan.edgesToAdd.push({ sourceId: operationId, targetId: sourcePostId });
  plan.edgesToAdd.push({ sourceId: operationId, targetId: destPostId });

  const transferRow: TransferRow = {
    id: row.id || deps.generateId(),
    sourceMaterialId: sourceId,
    destinationMaterialId: destinationId!,
    sourcePostMaterialId: sourcePostId,
    destinationPostMaterialId: destPostId,
    quantity: hasQuantity
      ? {
          value: { amount: row.amount!, units: row.unit! },
          type: quantityTypeFromUnit(row.unit!),
        }
      : undefined,
    symbolic: hasSymbolic ? row.symbolic! : undefined,
  };

  const transferOptions: TransferOptions = {
    quantityType: hasQuantity ? quantityTypeFromUnit(row.unit!) : 'Volume',
    transferEntireVolume: false,
    sameVolume: false,
    advancedOptionsEnabled: false,
  };

  plan.specPatch.transferRow = transferRow;
  plan.specPatch.transferOptions = transferOptions;
}

function planComplexTopology(
  graphData: GraphRecords,
  operationId: string,
  plan: ConversionPlan,
  keptInputIds: string[],
  keptOutputIds: string[],
  reusableReminderIds: string[],
  extras: ConversionExtras,
  deps: PlanDeps,
): void {
  if (keptInputIds.length === 0) {
    plan.blockers.push('A complex step needs at least one input material.');
    return;
  }
  if (keptOutputIds.length === 0) {
    plan.blockers.push('A complex step needs at least one output material.');
    return;
  }

  for (const inputId of keptInputIds) {
    plan.edgesToAdd.push({ sourceId: inputId, targetId: operationId });
  }
  for (const outputId of keptOutputIds) {
    plan.edgesToAdd.push({ sourceId: operationId, targetId: outputId });
  }

  // One reminder per input, reusing the operation's existing reminders in order
  // and creating the shortfall.
  const operation = graphData.operationNodes[operationId];
  keptInputIds.forEach((inputId, index) => {
    const inputMaterial = graphData.materialNodes[inputId];
    let reminderId = reusableReminderIds[index];

    if (!reminderId) {
      reminderId = deps.generateId();
      plan.materialsToCreate.push({
        id: reminderId,
        spec: {
          ...buildInputReminderNode(inputMaterial, operationId),
          position: offset(
            operation,
            -((keptInputIds.length * REMINDER_DX) / 2) + index * REMINDER_DX,
            POST_DY,
          ),
        },
      });
    } else {
      // A reused reminder may have been paired with a different input before
      // (or have carried TRANSFER remainder flags); re-point it at this one.
      plan.materialPatches.push({
        id: reminderId,
        patch: {
          isReminder: true,
          symbolicQuantity: 'remainder',
          sourceMaterials: [
            {
              sourceVertexId: inputId,
              name: inputMaterial.name,
              container: inputMaterial.container,
              notes: null,
            },
          ],
        },
      });
    }

    plan.edgesToAdd.push({ sourceId: operationId, targetId: reminderId });
  });

  // Reminders beyond the input count are surplus.
  for (const surplusId of reusableReminderIds.slice(keptInputIds.length)) {
    if (consumersOf(graphData, surplusId, operationId).length > 0) {
      plan.materialsToDetach.push(surplusId);
      plan.warnings.push(
        `Remainder "${nameOf(graphData, surplusId)}" is consumed by a later step, so it was kept and disconnected instead of deleted.`,
      );
    } else {
      plan.materialsToDelete.push(surplusId);
    }
  }

  const ioMapping =
    extras.ioMapping && extras.ioMapping.length > 0
      ? extras.ioMapping
      : keptInputIds.flatMap((inputMaterialId) =>
          keptOutputIds.map((outputMaterialId) => ({
            inputMaterialId,
            outputMaterialId,
            fraction: 1 / keptOutputIds.length,
          })),
        );

  const mappingError = validateIOMapping(ioMapping);
  if (mappingError) {
    plan.blockers.push(mappingError);
    return;
  }

  plan.specPatch.complexOptions = { ioMapping };
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Archetype-specific fields that must not survive the conversion. Mirrors the
 * clearing already done in `graph/domain/operationNode.ts`, which a collapsed
 * (edge-rendered) operation never reaches.
 */
function staleFieldsFor(
  to: Archetype,
  toClass: TopologyClass,
  target: ConversionTarget,
): Array<keyof OperationNodeSpec> {
  const fields: Array<keyof OperationNodeSpec> = [];
  if (to !== 'TRANSFER') fields.push('transferRow', 'transferOptions');
  if (to !== 'COMPLEX') fields.push('complexOptions');
  // `yield` is only read by calculateDownstreamQuantity for TRANSFORM/MEASURE.
  if (toClass !== 'SIMPLE') fields.push('yield');
  if (target.durationAllow === false) fields.push('duration');
  return fields;
}

function dedupe(ids: string[]): string[] {
  return Array.from(new Set(ids));
}

function nameOf(graphData: GraphRecords, materialId: string): string {
  return graphData.materialNodes[materialId]?.name ?? materialId;
}

function offset(
  operation: OperationNodeSpec | undefined,
  dx: number,
  dy: number,
): { x: number; y: number } {
  const base = operation?.position ?? { x: 0, y: 0 };
  return { x: base.x + dx, y: base.y + dy };
}
